<template>
	<div class="title-list" :class="{ 'full-width': fullWidth }">
		<HorizontalTitleList
			clickSource="sr-list"
			:additionalContexts="additionalContexts"
			:sponsoredAd="sponsoredAd"
			:emitOnScroll="true"
			:hasWatchNow="false"
			:impressionTrackingActive="false"
			:impressionTrackingCategory="impressionTrackingCategory"
			:impressionTrackingLabel="impressionTrackingLabel"
			:resetScrollOnChange="true"
			:showQuickActions="false"
			:showTvBadge="false"
			:titles="transformedTitles"
			:showRibbon="false"
			:imgsLoadCount="transformedTitles.length"
			@scrollbarClicked="trackScroll"
			@onPosterClick="$emit('onPosterClick', $event)"
			@onPosterClickOut="$emit('onPosterClickOut', $event)"
		>
			<template #poster="{ title, props, handlers }">
				<div>
					<PosterGraphql
						v-bind="transformPosterProps(props, title)"
						moduleGroup="sr_titlelist"
						v-on="handlers"
						@imageError="src => $emit('imageError', src)"
					/>
				</div>
			</template>
		</HorizontalTitleList>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';

import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import HorizontalTitleList from '@/components/timeline/HorizontalTitleList.vue';

import { ImpressionTrackingEvents } from '@/enums/events';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import type { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import type { HorizontalTitleListItem } from '@/components/timeline/types';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { ListTitleSR } from '@/features/sponsored-recommendations/types';

import { SraEventAction } from '@/@types/graphql-types';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';

const props = defineProps<{
	fullWidth: boolean;
	titles: ListTitleSR[];
	sponsoredAd: SponsoredAdFragment | undefined;
	additionalContexts?: SnowplowContext[];
	impressionTrackingLabel?: string;
	impressionTrackingProperty?: string;
	impressionTrackingCategory?: any;
	showSponsoredAd: boolean;
	placement?: ClickoutPlacement;
}>();

const emit = defineEmits<{
	(e: 'onPosterClick', data: { index: number; title: any; titleList?: string }): void;
	(e: 'onPosterClickOut', data: { index: number; title: any; titleList?: string; event: SraEventAction }): void;
	(e: 'imageError', src: string): void;
}>();

onMounted(() => {
	if (props.titles?.length) trackSimilarTitlesLoaded();
});

const posterOverrides = computed(() => {
	if (!props.sponsoredAd?.campaign) return null;

	const overrides = props.sponsoredAd?.campaign?.nodeOverrides ?? [];
	return Object.fromEntries(overrides.map(({ nodeId: id, promotionalImageUrl: url }) => [id, url]));
});

function transformPosterProps(posterProps: PosterGraphqlProps, title: HorizontalTitleListItem): PosterGraphqlProps {
	const posterUrl = posterOverrides.value?.[title.id];
	if (posterUrl == null) return posterProps;

	return {
		...posterProps,
		sponsoredRecImages: {
			imageUrl: posterUrl,
			sourceSets: [
				{
					srcSet: posterUrl?.replace(/\.\w+$/, '.avif'),
					imageProp: {
						extension: '.avif',
						type: 'image/avif',
					},
					sourceProp: {
						viewport: 'desktop',
					},
					media: null,
				},
			],
		},
		title: {
			...title,
			content: {
				...title.content,
				posterUrl,
			},
		},
	};
}

const transformedTitles = computed(() => {
	if (!props.sponsoredAd?.campaign) return props.titles;

	const overrides = props.sponsoredAd?.campaign?.nodeOverrides ?? [];

	const mapTitleIdWithUrl = Object.fromEntries(
		overrides.map(({ nodeId: id, watchNowOffer }) => [id, watchNowOffer?.standardWebURL])
	);

	// If we have a overwriten watchNowOffer unique to the title, we want to
	// inject that into the title object, replacing content.watchNowOffer
	return props.titles.map(title => {
		if (mapTitleIdWithUrl[title.id]) {
			title.content.fullPath = mapTitleIdWithUrl[title.id] || title.content.fullPath;
		}

		return title;
	});
});

function trackSimilarTitlesLoaded() {
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'loaded',
			nonInteraction: true,
			label: props.impressionTrackingLabel ?? undefined,
		},
		props.additionalContexts
	);
}

function trackScroll(label: string) {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'horizontal_scroller_clicked',
			label,

			property: props.impressionTrackingLabel ?? undefined,
		},
		props.additionalContexts
	);
}
</script>

<style lang="scss" scoped>
.title-list {
	margin-bottom: 20px;
	zoom: 0.5;
	@container (max-width : 500px) {
		zoom: 0.55;
	}
	&.full-width {
		zoom: 1;
		margin-bottom: 0px;
		@container (max-width : 500px) {
			margin-bottom: 20px;
		}
		:deep(.hidden-horizontal-scrollbar__items) {
			justify-content: space-between;
			min-width: calc(100% - 48px);
		}
	}
}
</style>
